/* eslint-disable @typescript-eslint/no-unused-expressions */
export const isSSR = (): boolean => typeof window === 'undefined';
export const isProd = (): boolean => process.env.ENV === 'prod';
export const isLocal = (): boolean => process?.env?.ENV === 'local';
export const isCSR = (): boolean => process?.env?.ENV === 'csr';

import * as appProps from '../../lib/config/config';
import { CAREMARK, RETAIL, SPECIALTY } from '../config/lobConfig';
import { nonProdLogoutKey, prodLogoutKey } from '../constants/apiAttributes';
import {
  CAREGIVING_SOURCE_NAME,
  ENTERPRISE_CAREGIVING_SOURCE_NAME,
  H4L_SOURCE_NAME,
  SELF_TIE_SOURCE_NAME
} from '../constants/serverSideAPIAttributes';
import { CAREGIVING_ACCESS_FLOW, ENTERPRISE_CAREGIVING_ACCESS_FLOW } from '../constants/serverSideAPIAttributes';
import { tags } from './analytics';
export const RXLABEL_FLOW_NAME = 'rxlabel';

export const getCurrentEnv = (origin = ''): string => {
  const lowerEnvArray = ['cte1', 'stp', 'sit1', 'sit2', 'sit3', 'qa1', 'qa2', 'qa3', 'local'];
  if (isSSR()) {
    //Need to know if this is CTE1 environment
    if (process.env?.ENV === null || process.env?.ENV === undefined) {
      return '';
    }
    if (origin && origin.toUpperCase().includes('CTE1')) {
      return 'CTE1';
    }
    const environment = process.env.ENV.toUpperCase();
    switch (process.env.ENV) {
      case 'local':
        return 'LOCAL';
      case 'prod':
      case 'prod-west':
        return 'PROD';
      case 'csr':
        return 'CSR';
      default:
        return environment ? environment : 'PROD';
    }
  }
  const host = window?.location?.hostname;
  const prodHost = [
    'm.cvs.com',
    'es.cvs.com',
    'www.cvs.com',
    'www.cvsspecialty.com',
    'www.caremark.com',
    'espanol.caremark.com'
  ];
  let env = 'PROD';
  if (prodHost.indexOf(host) > -1) return 'PROD';
  else if (host.includes('localhost')) return 'LOCAL';
  else if (host.includes('www-csr')) return 'CSR';
  else if (host.includes('qa-csr')) return 'QA2-CSR';
  else {
    for (const i of lowerEnvArray) {
      if (host && host.includes(i)) {
        env = i;
        break;
      }
    }
  }
  return env.toUpperCase();
};

export const isCVSMobileApp = (): boolean | undefined => {
  if (typeof window !== 'undefined') {
    if (
      window?.navigator?.userAgent.match(/CVS_iPAD_APP/i) ||
      window?.navigator?.userAgent.match(/CVS_ANDROID_APP/i) ||
      window?.navigator?.userAgent.match(/CVS_iPHONE_APP/i)
    ) {
      return true;
    }
    return false;
  }
};

export const formatTel = (e: any | FocusEvent): void => {
  const el = e.nativeEvent.target;
  const lettersRegex = /[a-z]/i;
  const specialCharsRegex = /[!$%^&*_+|~+'{}\[\]:V;<>?,.@#]/;
  if (el.value && (lettersRegex.test(el.value) || specialCharsRegex.test(el.value))) {
    el.value = '';
  }
  let pos = el.selectionStart;
  const skip = new Set([0, 1, 4, 5, 9]);
  const tel = el.value
    .replace('(', '')
    .replace(')', '')
    .replaceAll('-', '')
    .replace(/^\+?[0-1]/g, '')
    .replace(/\s/g, '');
  if (!tel) {
    el.value = '';
  } else {
    if (e.nativeEvent.inputType === 'deleteContentBackward') return;
    if (tel.length <= 2) {
      el.value = `(${tel.substring(0)})`;
    }
    if (tel.length >= 3 && tel.length <= 5) {
      el.value = `(${tel.substring(0, 3)}) ${tel.substring(3)}`;
    }
    if (tel.length >= 6) {
      el.value = `(${tel.substring(0, 3)}) ${tel.substring(3, 6)}-${tel.substring(6, 10)}`;
    }
    if (pos === 10 || pos === 6) {
      pos += 1;
    }
    if (pos === 5) {
      pos += 2;
    }
    while (skip.has(pos)) {
      pos += 1;
    }
    if (!('inputType' in e.nativeEvent)) return;
    el.selectionStart = pos;
    el.selectionEnd = pos;
  }
};

export const onlyNumberKey = (event: any) => {
  // allow copy, paste, select all etc.
  if (event.metaKey || event.controlKey) {
    return;
  }
  // allow shift, delete, tab etc.
  if (event.key?.length > 1) return;
  // don't allow any key except numbers.
  const regex = /[0-9]/;
  if (!regex.test(event.key)) event.preventDefault();
};

export const formatDate = (e: any | FocusEvent): void => {
  if (e.nativeEvent.inputType === 'deleteContentBackward') return;
  const el = e.nativeEvent.target;
  const dob = el.value.replace(/\//g, '');
  if (dob.length < 2) return;
  if (dob.length < 4) {
    el.value = [dob.substring(0, 2), dob.substring(2, 4)].join('/');
  } else {
    el.value = [dob.substring(0, 2), dob.substring(2, 4), dob.substring(4, 8)].join('/');
  }
};
// Parse MMDDYYYY string into components
export const parseDobString = (value: string) => {
  const mm = value?.substring(0, 2) || '';
  const dd = value?.substring(2, 4) || '';
  const yyyy = value?.substring(4, 8) || '';
  return { mm, dd, yyyy };
};

// convert date from MMDDYYYY to YYYY-MM-DD
export const convertDateFormatForAPI = (value: string) => {
  if (!value) return '';
  const { mm, dd, yyyy } = parseDobString(value);
  return `${yyyy}-${mm}-${dd}`;
};

// add 0s to the store number to make them 5 digit in API call
export const convertStoreNumberFormatForAPI = (value: string) => {
  if (value?.length < 4) value = '00' + value;
  else if (value?.length < 5) value = '0' + value;
  return value;
};

export const convertPhoneFormatForAPI = (value: string) => {
  if (value) {
    return value
      .replace('(', '')
      .replace(')', '')
      .replace('-', '')
      .replace(/^\+?[0-1]/g, '')
      .replace(/\s/g, '');
  }
  return '';
};

export const getOrigin = (): string => {
  const env = getCurrentEnv();
  if (env == 'LOCAL') {
    return 'https://www-qa2.cvs.com';
  }
  return `https://www${env?.toLowerCase() !== 'prod' ? '-' + env : ''}.cvs.com`;
};

export const checkReturnUrl = (url: string) => {
  // Check if the returnUrl is given as full url or path only
  if (url?.indexOf('http://') === 0 || url?.indexOf('https://') === 0) {
    const testURL = new URL(url);
    let host = '';
    if (testURL) host = testURL.host;
    // URL interface is not supported by Internet explorer yet
    // so added below logic for testURL undefined case at IE
    else {
      const l = document.createElement('a');
      l.href = url;
      host = l.hostname;
    }
    // check if host includes cvs.com
    if (host && ['.cvs.com', '.cvsspecialty.com', '.caremark.com'].find((element) => host.includes(element))) {
      return url;
      // if host doesn't include cvs.com or other LOBs then ignore it and redirect to home page
    } else return '/';
    // if it's not full url then return the path directly
  } else return url;
};

const clearSessionStorage = () => {
  sessionStorage?.removeItem('postReturnUrl');
  sessionStorage?.removeItem('cancelReturnUrl');
  sessionStorage?.removeItem('lookupFlow');
};

export const navigateBackToCallingApp = (flow?: string, addParam: string = '') => {
  let postReturnUrl = '/';
  const searchURL = sessionStorage.getItem('postReturnUrl');
  setTimeout(() => {
    if (flow === 'cancel') {
      postReturnUrl = sessionStorage.getItem('cancelReturnUrl');
      if (!postReturnUrl) {
        navigateBackToParentApp();
      }
    } else if (flow === 'lookupDone' || flow === 'lookupDoneRxLabel') {
      const appendingOp = searchURL.indexOf('?') > 0 ? '&' : '?';
      const lookupUrl = flow === 'lookupDoneRxLabel' ? 'lookup=done&lookupType=rxLabel' : 'lookup=done';
      postReturnUrl = searchURL + appendingOp + lookupUrl + addParam;
    } else {
      if (searchURL) {
        postReturnUrl = searchURL;
      }
    }
    const url = checkReturnUrl(postReturnUrl);
    clearSessionStorage();
    url && window.open(url, '_self');
  }, 500);
};

export const navigateToLogin = () => {
  const currentUrl = document.location;
  const loginUrl = `${window.origin}/account/login?returnUrl=${encodeURIComponent(currentUrl.toString())}`;
  const url = checkReturnUrl(loginUrl);
  url && window.location.assign(url);
};

export const isMinor = (dateOfBirth: string): boolean => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age < 18;
};

export const isFutureDate = (month: string, day: string, year: string): boolean => {
  const date = `${month}/${day}/${year}`;
  const digitCountMatch = /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/[0-9]{4}$/.test(date);
  const enteredMonth = parseInt(month) - 1;
  const enteredDate = new Date(parseInt(year), enteredMonth, parseInt(day));
  const currentDate = new Date();
  const futureDate = enteredDate > currentDate;
  if (digitCountMatch && futureDate) {
    return true;
  }
  return false;
};

export const getPostSearchUrl = (returnUrl = ''): string => {
  const searchParams = new URLSearchParams(window.location.search);
  /* check if url or session storage contains postSearchUrl */
  const postSearchUrl = !searchParams.get('postSearchUrl')
    ? returnUrl || sessionStorage.getItem('postReturnUrl') || searchParams.get('returnUrl') || ''
    : searchParams.get('postSearchUrl') || '';
  return checkReturnUrl(postSearchUrl);
};

export const triggerEvent = (data: any) => {
  const event = new CustomEvent('cvsAnalyticsEvent', { detail: data });
  document.dispatchEvent(event);
};

export const isMobile = (): boolean =>
  isSSR() ? false : window.navigator.userAgent.toLowerCase().indexOf('mobi') !== -1;

export const getCookie = (name) => {
  const nameEQ = name + '=';
  if (document) {
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
  }
  return null;
};
/**
 * @returns void
 * @description: a helper method that create cookie
 */
export const setCookie = (key: string, value: string) => {
  document.cookie = `${key}=${value}`;
};

export const getSourceNameForApi = (flow: string) => {
  let sourceName = CAREGIVING_SOURCE_NAME.toLowerCase();
  switch (flow) {
    case CAREGIVING_ACCESS_FLOW:
      sourceName = CAREGIVING_SOURCE_NAME;
      break;
    case ENTERPRISE_CAREGIVING_ACCESS_FLOW:
      sourceName = ENTERPRISE_CAREGIVING_SOURCE_NAME;
      break;
    case tags.self:
      sourceName = SELF_TIE_SOURCE_NAME;
      break;
    case tags.h4l:
      sourceName = H4L_SOURCE_NAME;
      break;
  }
  return sourceName;
};

export const clearLookupStorage = () => {
  localStorage.removeItem('cgId');
  localStorage.removeItem('lookupResponse');
};

// Make first letter capitial for a word
export const capitalizeFirstLetter = (word) => {
  if (word && typeof word !== 'string' && word.trim() !== '') {
    word = word?.toLowerCase();
    return word.charAt(0).toUpperCase() + word.slice(1);
  }
  return word;
};

export const getCvsHeader = (type: string) => ({
  apiKey: getCurrentEnv() !== '' ? nonProdLogoutKey : prodLogoutKey,
  appName: 'CVS_WEB',
  channelName: 'WEB',
  deviceToken: 'd9708df38d23192e',
  deviceType: 'DESKTOP',
  responseFormat: 'JSON',
  securityType: 'apiKey',
  source: 'CVS_WEB',
  lineOfBusiness: 'RETAIL',
  type
});

export const addZero = (event: any) => {
  if (event.target.value < 1) return;
  if (event.target.value.length < 2) event.target.value = 0 + event.target.value;
};

export const formatPhoneNumber = (phNum: string) => {
  const cleaned = ('' + phNum).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return null;
};

export const validateDate = (month?: string, day?: string, year?: string) => {
  if (month?.trim() === '' && day?.trim() === '' && year?.trim() === '') return false;
  const validator = [false, false, false];
  if (Number(month) > 12) validator[0] = true;
  if (Number(day) > 31) validator[1] = true;
  return validator;
};

export const validateYear = (dateOfBirth: string) => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age < 200;
};

export const datePatternMatch = (month: string, day: string, year: string) => {
  if (month.length < 2) month = 0 + month;
  if (day.length < 2) day = 0 + day;
  const date = `${month}/${day}/${year}`;
  return /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/.test(date);
};

export const navigateBackToParentApp = (flow?: string) => {
  let postSearchURL = sessionStorage.getItem('postSearchURL') || '/';
  const appendingOp = postSearchURL?.indexOf('?') > 0 ? '&' : '?';
  setTimeout(() => {
    if (flow === 'lookupDone') {
      postSearchURL += appendingOp + `lookup=done`;
    } else if (flow === 'mfaDone') {
      postSearchURL += appendingOp + `mfa=done`;
    }
    const url = checkReturnUrl(postSearchURL);
    clearSessionStorage();
    url && window.open(url, '_self');
  }, 500);
};

export const getFlowProps = (flow: any) => {
  return appProps[flow];
};

export const getAdobeFlowName = (source?: any) => {
  const flow = source || sessionStorage.getItem('lookupFlow');
  const flowName = flow ? (typeof flow == 'string' ? flow : JSON.parse(flow)) : '';
  const props = appProps[flowName];
  return props?.adobeFlowName;
};

export const isCaregivingAccessFlow = (flow?: string): boolean => {
  if (!flow) return false;
  return flow === CAREGIVING_ACCESS_FLOW || flow === ENTERPRISE_CAREGIVING_ACCESS_FLOW;
};

export const handleAddManagePeople = () => {
  const hostname = getOrigin();
  const searchParam = window?.location?.search;
  const url = new URL(`${hostname}${'/account/manage-family'}${searchParam}`);
  url && window.location.assign(url);
};

/**
 *
 * @param url
 * @param tag
 * @returns void
 * @description util functon to load run time js file.
 */
export const loadScript = (url: string, tag: string) => {
  return new Promise<any>((resolve, reject) => {
    if (!document) return;
    if (document.getElementById(tag)) return true;
    const s = document.createElement('script') as HTMLScriptElement;
    s.src = url;
    s.id = tag;
    document.body.appendChild(s);
    s.async = true;
    s.onload = (e) => {
      resolve(e);
    };
    s.onerror = (e) => {
      reject(e);
    };
  });
};

export const removePhNumberFormat = (phNum: string) => {
  if (phNum) {
    const cleaned = ('' + phNum).replace(/\D/g, '');
    return cleaned;
  }
  return '';
};

export const isSpecialty = (): boolean => {
  return typeof window !== 'undefined' && /^(?:[^.]+\.)?cvsspecialty\.com(\/.*)?$/.test(window.location.hostname);
};

export const isCaremark = (): boolean => {
  return typeof window !== 'undefined' && /^(?:[^.]+\.)?caremark\.com(\/.*)?$/.test(window.location.hostname);
};

export const isRetail = (): boolean => {
  return typeof window !== 'undefined' && /^(?:[^.]+\.)?cvs\.com(\/.*)?$/.test(window.location.hostname);
};

export const RETAIL_LOB = 'retail';
export const SPECIALTY_LOB = 'SPECIALTY';
export const CAREMARK_LOB = 'CAREMARK';

export const findLob = (LOB: string = RETAIL_LOB) => {
  let lob = LOB;

  if (isCaremark()) {
    lob = CAREMARK_LOB;
  } else if (isSpecialty()) {
    lob = SPECIALTY_LOB;
  } else if (isRetail()) {
    lob = RETAIL_LOB;
  }
  return lob;
};

export const getLobData = () => {
  const lobData = {
    [SPECIALTY_LOB.toLowerCase()]: SPECIALTY,
    [CAREMARK_LOB.toLowerCase()]: CAREMARK,
    [RETAIL_LOB.toLowerCase()]: RETAIL
  };
  return lobData[findLob().toLowerCase()];
};

export const getAdrumConfigSpace = (env: string) => {
  if (isCaremark()) {
    return env === 'prod' ? 'AD-AAB-ABG-SZM' : 'AD-AAB-ABR-EPH';
  } else if (isSpecialty()) {
    return env === 'prod' ? 'AD-AAB-ABG-SYR' : 'AD-AAB-ACG-GTF';
  } else {
    return env === 'prod' ? 'AD-AAB-ABG-SWZ' : 'AD-AAB-ACE-ZYJ';
  }
};

export const getLDQueryParams = () => {
  if (isSSR()) {
    return;
  }
  const searchParam = window?.location?.search;
  const urlParams = new URLSearchParams(searchParam);
  return urlParams.get('location');
};

export const domainNameForTags = () => {
  let tag = 'cvs';
  const lob = findLob();
  if (lob === SPECIALTY_LOB) {
    tag = 'cvssp';
  } else if (lob === CAREMARK_LOB) {
    tag = 'cvscmk';
  }
  return tag;
};

export const submitForm = (formData) => {
  const form = document.getElementById('registrationForm') as HTMLFormElement;
  const inputElement = form.children[0] as HTMLInputElement;
  inputElement.value = formData;
  form.submit();
};

export const isParsableJson = (str: string) => {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
};

// Safely parse JSON strings. If value is not a string or parsing fails, return fallback.
export const safeJsonParse = <T>(value: unknown, fallback: T): T => {
  if (typeof value !== 'string') return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

// eslint-disable-next-line no-undef
export const openLinkInNewTab = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
  e.preventDefault();
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
};

// Helper to assemble date string
export const assembleDateOfBirth = (values) => {
  const { dateOfBirth } = values;
  return dateOfBirth || '';
};
